<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { ConsentRequest } from "@types"
import type { Address } from "@types"
import type { MultiConsentBuildingRecap } from "@core/types/source/multi_consent"
import type { Services } from "@services"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type { SourceFormMultiBuildingItem } from "@core/machines"
import { SOURCES_TYPES } from "@core/constants"
import { formatAddress, formatIdSource } from "@core/helpers"
import { BuildingService } from "@core/services"
import { ConsentRequestService } from "@core/services"
import { rafraichirPlafondServi } from "@core/composables/plafond-rafraichissement"
import type { CreateConsentRequestMultiResponse } from "@core/services/consentrequest.service"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Signature des consentements",
            ownerTitle: "Titulaire",
            buildingsTitleWithCount:
                "{buildings} bâtiment(s) • {meters} compteur(s)",
            ownerInfo: "Informations du titulaire",
            ownerCompanyTypeEntreprise: "Entreprise",
            ownerCompanyTypeAsso: "Association",
            ownerCompanyTypeCollectivite: "Collectivité",
            ownerCompanyTypeCopro: "Copropriété",
            ownerUser: "Représentant / Client",
            invalid_title: "Certains compteurs ne peuvent pas être signés",
            invalid_message_one:
                "1 compteur possède déjà un consentement signé par un autre utilisateur sur l'application. Si vous voulez avoir accès à ce compteur veuillez faire la demande auprès des personnes possédant les droits sur ces compteurs.",
            invalid_message_many:
                "{count} compteurs possèdent déjà des consentements signés par d'autres utilisateurs sur l'application. Si vous voulez avoir accès à ces compteurs veuillez faire la demande auprès des personnes possédant les droits sur ces compteurs.",
            invalid_list: "Liste des compteurs n'ayant pas pu être signés :",
            invalid_continue:
                "Cliquez sur « Valider » pour ajouter les autres compteurs.",
            all_excluded_title: "Aucun compteur ne peut être ajouté",
            all_excluded_message:
                "Tous les compteurs saisis sont déjà suivis avec le consentement d'une autre entreprise. Si vous souhaitez y avoir accès, veuillez en faire la demande auprès des personnes qui détiennent les droits sur ces compteurs. Les bâtiments préparés pour cet ajout seront retirés.",
            all_excluded_list: "Compteurs concernés :",
            return: "Retour",
            submit: "Valider",
            close: "Fermer",
            rollbackInProgress:
                "Annulation en cours : suppression des bâtiments créés…",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type ConsentType = "pdl" | "pce" | "both"

/**
 * Clés i18n par type d'organisation (aligné sur `CompanyType`)
 */
const OWNER_COMPANY_TYPE_LABEL_KEYS = {
    entreprise: "ownerCompanyTypeEntreprise",
    asso: "ownerCompanyTypeAsso",
    collectivite: "ownerCompanyTypeCollectivite",
    copro: "ownerCompanyTypeCopro",
} as const satisfies Record<CompanyType, string>

interface Props {
    /**
     * Identifiant de la demande multi (déjà créée au récap si tiers).
     * Vide pour le titulaire connecté : création bâtiments + demande au clic Signer.
     */
    requestId: string
    /** Données titulaire pour POST consentrequestmulti (parcours connecté) */
    owner: OwnerOutput
    /** Bâtiments sources (PDL/PCE + localisation) pour création API si pas de `requestId` */
    buildingsCreation: SourceFormMultiBuildingItem[]
    /** Liste des bâtiments avec leurs compteurs */
    buildings?: MultiConsentBuildingRecap[]
    /**
     * Repli si aucune source dans `buildings` / `buildingsCreation` (ex. story).
     * Sinon le type est déduit : PDL + PCE dans le parcours → `both`.
     */
    type?: ConsentType
    /** Durée du consentement en mois */
    duration?: number
    /** Demandes invalides (compteurs déjà signés par un autre) */
    invalidConsentRequests?: ConsentRequest[]
    /** Désactive le collapse de la section bâtiments (tout afficher ouvert) */
    noCollapse?: boolean
}

interface Emits {
    (e: "done:sign"): void
    /**
     * Émis lors du clic sur le bouton retour
     */
    (e: "click:return"): void
    /**
     * Émis quand l'utilisateur quitte le parcours sans rien signer (tous les
     * compteurs écartés, DEV-2333) : le parent ferme le parcours.
     */
    (e: "click:quit"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    buildings: () => [],
    buildingsCreation: () => [],
    type: "pdl",
    duration: 60,
    invalidConsentRequests: () => [],
    noCollapse: false,
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!
const buildingService = new BuildingService()
const consentRequestService = new ConsentRequestService()

// ================================
// STATE
// ================================
/** True quand toutes les conditions de consentement sont acceptées (SourceConsentSignConditions) */
const consentConditionsAccepted = ref(false)
/** Collapsibles récap (alignés sur SourceMultiAppendFormSummary) */
const isOwnerOpen = ref(false)
const isBuildingsOpen = ref(false)
/** Chargement en cours lors du submit */
const loading = ref(false)
/** Message d'erreur API */
const error = ref("")
/** True pendant le rollback (suppression des bâtiments après échec création demande). */
const isRollingBack = ref(false)
/** Invalides retournés par la création de la demande (titulaire connecté) */
const invalidConsentRequestsAfterCreate = ref<
    Array<{ idsource: string; idtype: string }>
>([])
/**
 * Demande multi créée par ce composant mais pas encore signée, parce que l'API a
 * écarté des compteurs (DEV-2333). « Valider » la signe ensuite, sans recréer
 * les bâtiments.
 */
const createdRequestIdPendingSign = ref("")
/**
 * Tous les compteurs ont été écartés : la demande créée est vide, elle n'est
 * jamais signée (DEV-2333, R2).
 */
const isAllExcluded = ref(false)
/** Bâtiments créés par ce composant (pour les retirer si rien n'est signé). */
const createdBuildingIdsOnScreen = ref<string[]>([])

/**
 * Une demande attend sa signature après l'avertissement : « Retour » est
 * indisponible (DEV-2333, R1). Revenir démonterait l'étape, perdrait la
 * demande et recréerait bâtiments et demande à la validation suivante. Les
 * retirer au retour ne serait pas propre : la création de la demande a déjà
 * posé les liens de facturation des compteurs retenus, que ni la suppression
 * des bâtiments ni celle de la demande ne lèvent.
 */
const getIsReturnLocked = computed(
    () => createdRequestIdPendingSign.value !== "" && !isAllExcluded.value
)

// ================================
// COMPUTED
// ================================
/**
 * Nombre total de compteurs (sources), aligné sur SourceMultiAppendFormSummary
 */
const getTotalSources = computed(() =>
    (props.buildings ?? []).reduce(
        (acc, b) => acc + (b.sources?.length ?? 0),
        0
    )
)

/**
 * Adresse formatée de l'entreprise
 */
const getCompanyAddress = computed(() => {
    return props.owner.company?.address
        ? formatAddress(props.owner.company.address)
        : ""
})

/**
 * Libellé du type d'organisation (Entreprise, Association, Collectivité, Copropriété)
 */
const getOwnerCompanyTypeLabel = computed(() => {
    const type = props.owner.company?.type
    if (!type) return ""
    const key = OWNER_COMPANY_TYPE_LABEL_KEYS[type as CompanyType]
    return key ? t(key) : type
})

/** Liste bâtiments toujours dépliée (story / variante sans collapse) */
watch(
    () => props.noCollapse,
    (v) => {
        if (v) isBuildingsOpen.value = true
    },
    { immediate: true }
)

/** Invalides affichés (props API ou création locale) */
const getInvalidConsentRequestsResolved = computed(
    (): Array<ConsentRequest | { idsource: string; idtype: string }> => {
        return invalidConsentRequestsAfterCreate.value.length > 0
            ? invalidConsentRequestsAfterCreate.value
            : props.invalidConsentRequests
    }
)

/**
 * Type affiché dans `SourceConsentSignConditions` : `both` si le parcours contient au moins un PDL et un PCE.
 */
const getConsentConditionsType = computed((): ConsentType => {
    const idtypes = new Set<string>()
    for (const b of props.buildings ?? []) {
        for (const s of b.sources ?? []) {
            idtypes.add(String(s.idtype).toLowerCase())
        }
    }
    for (const b of props.buildingsCreation ?? []) {
        for (const s of b.sources ?? []) {
            idtypes.add(String(s.idtype).toLowerCase())
        }
    }
    if (idtypes.size === 0) return props.type
    const hasPdl = idtypes.has(SOURCES_TYPES.PDL)
    const hasPce = idtypes.has(SOURCES_TYPES.PCE)
    if (hasPdl && hasPce) return "both"
    if (hasPdl) return "pdl"
    if (hasPce) return "pce"
    return props.type
})

/** Message invalides (singulier / pluriel) */
const getInvalidMessage = computed(() =>
    (getInvalidConsentRequestsResolved.value?.length ?? 0) <= 1
        ? t("invalid_message_one")
        : t("invalid_message_many", {
              count: getInvalidConsentRequestsResolved.value?.length ?? 0,
          })
)

/** Liste des compteurs invalides formatés pour affichage */
const getInvalidMetersFormatted = computed(() =>
    (getInvalidConsentRequestsResolved.value ?? [])
        .map((req) => {
            const r = req as {
                idsource?: string
                idtype?: string
                source?: { idsource?: string; idtype?: string }
            }
            const idsource = r.source?.idsource ?? r.idsource ?? ""
            const idtype = r.source?.idtype ?? r.idtype ?? "pdl"
            return formatIdSource(idsource, idtype)
        })
        .filter(Boolean)
        .join(" • ")
)

/**
 * Chargement soumission ou rollback (bouton Valider bloqué pendant la suppression des bâtiments).
 */
const getIsSignBusy = computed(() => loading.value || isRollingBack.value)

// ================================
// INTERACTIONS
// ================================
/**
 * Indique si la demande multi existe déjà (parcours tiers après récap).
 */
function getHasExistingRequestId(): boolean {
    return typeof props.requestId === "string" && props.requestId.trim() !== ""
}

/**
 * Compteurs écartés par l'API à la création de la demande multi (consentement déjà
 * signé par une autre organisation). L'API les rend dans `invalids`
 * (`ConsentRequestMulti.format`) ; `invalidConsentRequests`, seul champ lu jusqu'ici,
 * n'est jamais rendu (DEV-2333). Les deux sont lus.
 */
function getCreatedRequestInvalids(
    data: CreateConsentRequestMultiResponse | null | undefined
): Array<{ idsource: string; idtype: string }> {
    const list = data?.invalids ?? data?.invalidConsentRequests
    return Array.isArray(list) ? list : []
}

/**
 * Crée bâtiments + demande multi, puis met la demande à jour (signée).
 * Rollback : suppression des bâtiments si la création de la demande échoue après les bâtiments.
 */
async function runCreateBuildingsRequestAndSign(): Promise<void> {
    if (!props.owner?.user) {
        throw new Error("Données titulaire incomplètes")
    }
    let createdBuildingIds: string[] = []
    try {
        const createdBuildings = await buildingService.createBuildings(
            props.buildingsCreation.map((building) => ({
                name: building.name,
                address: building.address,
                location: {
                    coordinates: building.location ?? [],
                },
            }))
        )
        createdBuildingIds = createdBuildings.map((b) => b._id)

        const consentRequests = createdBuildings.flatMap((building, index) => {
            return (
                props.buildingsCreation[index]?.sources.map((source) => ({
                    address: building.address,
                    idtype: source.idtype as "pdl" | "pce",
                    idsource: source.idsource,
                    date_from_data: source.date_from_data || null,
                    _id_building: building._id,
                })) || []
            )
        })

        const data = await consentRequestService.createConsentRequestMulti({
            user: props.owner.user,
            company: props.owner.company || null,
            consentRequests,
        })

        const invalids = getCreatedRequestInvalids(data)
        if (invalids.length > 0) {
            invalidConsentRequestsAfterCreate.value = invalids
            createdBuildingIdsOnScreen.value = createdBuildingIds
            const retenues = Array.isArray(data?.consentrequests)
                ? data.consentrequests.length
                : consentRequests.length - invalids.length
            if (retenues <= 0) {
                // R2 : rien à signer ; on ne signe jamais une demande vide
                isAllExcluded.value = true
                return
            }
            // la demande existe (sans les compteurs écartés) : on la garde pour la
            // signer au prochain « Valider », après que l'utilisateur a vu la liste
            createdRequestIdPendingSign.value = data._id
            return
        }

        await services.$updateConsentRequestMultiById(data._id)
        void rafraichirPlafondServi()
        emit("done:sign")
    } catch (e) {
        await rollbackCreatedBuildings(createdBuildingIds)
        throw e
    }
}

/**
 * Retire les bâtiments créés par cet écran (rollback existant, réutilisé).
 * @param ids - Identifiants des bâtiments créés
 */
async function rollbackCreatedBuildings(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    isRollingBack.value = true
    try {
        await Promise.all(ids.map((id) => buildingService.deleteBuilding(id)))
    } finally {
        isRollingBack.value = false
    }
}

/**
 * Tous les compteurs écartés (R2) : les bâtiments créés pour cet ajout ne
 * portent aucun compteur ; on les retire puis on revient en arrière (pour
 * corriger la saisie) ou on quitte le parcours. Rien n'est signé.
 * @param sortie - `return` (étape précédente) ou `quit` (fermer le parcours)
 */
async function onLeaveAllExcluded(sortie: "return" | "quit"): Promise<void> {
    if (getIsSignBusy.value) return
    try {
        error.value = ""
        await rollbackCreatedBuildings(createdBuildingIdsOnScreen.value)
        createdBuildingIdsOnScreen.value = []
        if (sortie === "return") emit("click:return")
        else emit("click:quit")
    } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : String(err)
    }
}

/**
 * Soumission : mise à jour demande existante (tiers) ou création complète puis signature (connecté).
 */
async function onSubmit(): Promise<void> {
    if (!consentConditionsAccepted.value || getIsSignBusy.value) return
    try {
        error.value = ""
        loading.value = true
        if (getHasExistingRequestId()) {
            invalidConsentRequestsAfterCreate.value = []
            await services.$updateConsentRequestMultiById(props.requestId)
            void rafraichirPlafondServi()
            emit("done:sign")
        } else if (createdRequestIdPendingSign.value) {
            // compteurs écartés déjà montrés : on signe la demande créée au clic
            // précédent (la liste reste affichée jusqu'à la fin)
            await services.$updateConsentRequestMultiById(
                createdRequestIdPendingSign.value
            )
            createdRequestIdPendingSign.value = ""
            void rafraichirPlafondServi()
            emit("done:sign")
        } else {
            invalidConsentRequestsAfterCreate.value = []
            await runCreateBuildingsRequestAndSign()
        }
    } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : String(err)
    } finally {
        loading.value = false
    }
}

/**
 * Gère le clic sur le header du collapsible titulaire
 */
function onClickOwnerHeader(): void {
    isOwnerOpen.value = !isOwnerOpen.value
}

/**
 * Gère le clic sur le header du collapsible bâtiments
 */
function onClickBuildingsHeader(): void {
    if (props.noCollapse) return
    isBuildingsOpen.value = !isBuildingsOpen.value
}

/**
 * Retour à l’étape précédente (désactivé pendant la soumission ou le rollback).
 */
function onReturn(): void {
    if (getIsSignBusy.value || getIsReturnLocked.value) return
    if (isAllExcluded.value) {
        void onLeaveAllExcluded("return")
        return
    }
    emit("click:return")
}
</script>

<template>
    <div class="source-form-multi-consent-sign flex flex-col gap-6 w-full">
        <!-- ================================ -->
        <!-- TITRE (aligné SourceMultiAppendFormSummary) -->
        <!-- ================================ -->
        <div class="flex flex-col gap-2 text-center">
            <h2 class="text-xl font-semibold text-gray-700">
                {{ t("title") }}
            </h2>
        </div>

        <!-- ================================ -->
        <!-- COLLAPSIBLE TITULAIRE -->
        <!-- ================================ -->
        <div class="flex flex-col border-b border-gray-200 pb-6">
            <div
                class="flex items-center justify-between w-full cursor-pointer py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                role="button"
                tabindex="0"
                :aria-expanded="isOwnerOpen"
                aria-controls="source-form-multi-consent-sign-owner-panel"
                @click="onClickOwnerHeader"
                @keydown.enter.prevent="onClickOwnerHeader"
                @keydown.space.prevent="onClickOwnerHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{ t("ownerTitle") }}
                </h3>
                <BaseIcon
                    :icon="
                        isOwnerOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'
                    "
                    class="text-gray-500"
                />
            </div>

            <div
                v-show="isOwnerOpen"
                id="source-form-multi-consent-sign-owner-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <p class="text-sm text-gray-500">{{ t("ownerInfo") }}</p>

                <div
                    v-if="owner.company"
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span class="text-xs font-medium text-gray-500">
                        {{ getOwnerCompanyTypeLabel }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.company.name }}
                        <template v-if="getCompanyAddress">
                            • {{ getCompanyAddress }}
                        </template>
                        <template
                            v-if="
                                owner.company.type !== 'collectivite' &&
                                owner.company.idcompany
                            "
                        >
                            • {{ owner.company.idcompany }}
                        </template>
                    </p>
                </div>

                <div
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span
                        v-if="owner.company"
                        class="text-xs font-medium text-gray-500"
                    >
                        {{ t("ownerUser") }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.user.firstname }} {{ owner.user.lastname }} •
                        {{ owner.user.username?.toLowerCase() }}
                    </p>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- COLLAPSIBLE BÂTIMENTS -->
        <!-- ================================ -->
        <div class="flex flex-col pb-6">
            <div
                class="flex items-center justify-between w-full py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                :class="noCollapse ? 'cursor-default' : 'cursor-pointer'"
                role="button"
                tabindex="0"
                :aria-expanded="noCollapse || isBuildingsOpen"
                aria-controls="source-form-multi-consent-sign-buildings-panel"
                @click="onClickBuildingsHeader"
                @keydown.enter.prevent="onClickBuildingsHeader"
                @keydown.space.prevent="onClickBuildingsHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{
                        t("buildingsTitleWithCount", {
                            buildings: buildings.length,
                            meters: getTotalSources,
                        })
                    }}
                </h3>
                <BaseIcon
                    v-if="!noCollapse"
                    :icon="
                        isBuildingsOpen
                            ? 'pi pi-chevron-up'
                            : 'pi pi-chevron-down'
                    "
                    class="text-gray-500"
                />
            </div>

            <div
                v-show="noCollapse || isBuildingsOpen"
                id="source-form-multi-consent-sign-buildings-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <div
                    v-for="(building, index) in buildings"
                    :key="`building-${index}`"
                    class="p-4 border border-gray-200 rounded bg-gray-50 hover:border-cyan-400 transition-colors"
                >
                    <ItemBuildingRecap
                        :name="building.name"
                        :address="(building.address ?? null) as Address"
                        :pdl="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PDL)
                                .map((s) => s.idsource)
                        "
                        :pce="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PCE)
                                .map((s) => s.idsource)
                        "
                    />
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- ALERTE COMPTEURS INVALIDES -->
        <!-- ================================ -->
        <BaseMessage
            v-if="isAllExcluded"
            severity="warn"
            :closable="false"
            class="source-form-multi-consent-sign__all-excluded w-full"
        >
            <p class="font-medium">{{ t("all_excluded_title") }}</p>
            <p>{{ t("all_excluded_message") }}</p>
            <p>{{ t("all_excluded_list") }}</p>
            <p class="text-sm mt-1">{{ getInvalidMetersFormatted }}</p>
        </BaseMessage>
        <BaseMessage
            v-else-if="getInvalidConsentRequestsResolved.length > 0"
            severity="warn"
            :closable="false"
            class="source-form-multi-consent-sign__invalid w-full"
        >
            <p class="font-medium">{{ t("invalid_title") }}</p>
            <p>{{ getInvalidMessage }}</p>
            <p>{{ t("invalid_list") }}</p>
            <p class="text-sm mt-1">{{ getInvalidMetersFormatted }}</p>
            <p v-if="createdRequestIdPendingSign" class="mt-2">
                {{ t("invalid_continue") }}
            </p>
        </BaseMessage>

        <!-- ================================ -->
        <!-- CONDITIONS DE CONSENTEMENT -->
        <!-- ================================ -->
        <SourceConsentSignConditions
            v-model="consentConditionsAccepted"
            class="w-full"
            :type="getConsentConditionsType"
            :duration="duration"
        />

        <!-- ================================ -->
        <!-- ROLLBACK EN COURS -->
        <!-- ================================ -->
        <BaseMessage
            v-if="isRollingBack"
            severity="info"
            :closable="false"
            class="source-form-multi-consent-sign__rollback w-full"
        >
            <p class="font-medium">{{ t("rollbackInProgress") }}</p>
        </BaseMessage>

        <!-- ================================ -->
        <!-- FOOTER : RETOUR + VALIDER -->
        <!-- ================================ -->
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between w-full"
        >
            <BaseButton
                :label="t('return')"
                severity="secondary"
                class="min-w-[10rem] w-full md:w-auto"
                :disabled="getIsSignBusy || getIsReturnLocked"
                @click="onReturn"
            />
            <BaseButton
                v-if="isAllExcluded"
                :label="t('close')"
                class="min-w-[10rem] w-full md:w-auto"
                :loading="getIsSignBusy"
                :disabled="getIsSignBusy"
                @click="onLeaveAllExcluded('quit')"
            />
            <BaseButton
                v-else
                :label="t('submit')"
                class="min-w-[10rem] w-full md:w-auto"
                :loading="getIsSignBusy"
                :disabled="!consentConditionsAccepted || getIsSignBusy"
                @click="onSubmit"
            />
        </div>

        <BaseMessage
            v-if="error"
            severity="error"
            :closable="false"
            class="w-full"
        >
            {{ error }}
        </BaseMessage>
    </div>
</template>
